import { DateValue } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { useCallback, useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { CaseTemplateStatus } from 'dashboard/models/case-template';
import {
  CaseAnalyticsFilterDateType,
  clearCaseAnalytics,
  resetCaseAnalyticsDateFilter,
  setCaseAnalyticsFilters,
} from 'dashboard/redux/actions/case-analytics';
import { showMessage } from 'dashboard/redux/actions/messages';
import { initialState } from 'dashboard/redux/reducers/case-analytics';
import { getAdjustedCaseAnalyticsFilters } from 'dashboard/redux/selectors/case';
import { RootState } from 'dashboard/redux/store';
import {
  loadCaseAnalyticsResolutionDistribution,
  loadCaseAnalyticsResolutionsByTag,
  loadCaseAnalyticsTagDistribution,
} from 'dashboard/redux/thunk-actions/case-analytics';
import { loadCaseTemplateBasicsOptions } from 'dashboard/redux/thunk-actions/case-templates';
import { loadTags } from 'dashboard/redux/thunk-actions/tags';
import { loadTeams } from 'dashboard/redux/thunk-actions/teams';
import { loadUserList } from 'dashboard/redux/thunk-actions/user';
import DashboardCaseAnalyticsResolutionsView from 'dashboard/views/DashboardCaseAnalyticsResolutionsView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import { ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { Iso8601DateString } from 'lib/types';
import { getDateRange, updateDateFilters } from 'lib/utils/filters';

const DashboardCaseAnalyticsResolutionsRoute = (props: PropsFromRedux) => {
  const { currentEnvironment, filters } = props;
  const {
    loading: loadingDistribution,
    error: loadDistributionError,
    callback: loadCaseResolutionsAnalyticsDistribution,
  } = useApiCallback(async () => {
    await props.loadCaseAnalyticsResolutionDistribution({ currentEnvironment, filters });
  }, [currentEnvironment, filters]);

  const {
    loading: loadingTagDistribution,
    error: loadTagDistributionError,
    callback: loadCaseResolutionsAnalyticsTagDistribution,
  } = useApiCallback(async () => {
    await props.loadCaseAnalyticsTagDistribution({ currentEnvironment, filters });
  }, [currentEnvironment, filters]);

  const {
    loading: loadingResolutionByTag,
    error: loadResolutionByTagError,
    callback: loadCaseResolutionsAnalyticsByTag,
  } = useApiCallback(async () => {
    await props.loadCaseAnalyticsResolutionsByTag({ currentEnvironment, filters });
  }, [currentEnvironment, filters]);

  // only fetch if there is no data
  useEffect(() => {
    if (props.caseResolutionDistribution === initialState.caseResolutionDistribution) {
      loadCaseResolutionsAnalyticsDistribution();
      loadCaseResolutionsAnalyticsTagDistribution();
      loadCaseResolutionsAnalyticsByTag();
    }
  }, []);

  useApiErrorHandler({
    error: loadDistributionError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(
          "You don't have permissions for an action on this page: View case analytics",
          MessageType.Failure,
        );
      } else {
        props.showMessage('Failed to load case analytics.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiErrorHandler({
    error: loadTagDistributionError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(
          "You don't have permissions for an action on this page: View case analytics",
          MessageType.Failure,
        );
      } else {
        props.showMessage('Failed to load case analytics.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiErrorHandler({
    error: loadResolutionByTagError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(
          "You don't have permissions for an action on this page: View case analytics",
          MessageType.Failure,
        );
      } else {
        props.showMessage('Failed to load case analytics.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: loadingCaseTemplateBasicsOptions } = useApiLoader({
    loader: props.loadCaseTemplateBasicsOptions,
    deps: [props.loadCaseTemplateBasicsOptions],
    onError: () => props.showMessage('Failed to load case templates.', MessageType.Failure),
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: loadingTags } = useApiLoader({
    loader: () => props.loadTags(ApiModel.Case),
    deps: [props.loadTags],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load tags.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: loadingUsers } = useApiLoader({
    loader: () => props.loadUserList(),
    deps: [props.loadUserList],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load users.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: loadingTeams } = useApiLoader({
    loader: () => props.loadTeams(),
    deps: [props.loadTeams],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load teams.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const handleDateUpdate = useCallback(
    (range: { startDate?: Iso8601DateString; endDate?: Iso8601DateString }) => {
      const { start, end } = getDateRange(range.startDate, range.endDate);
      props.setCaseAnalyticsFilters({
        startDate: start,
        endDate: end,
      });
    },
    [],
  );

  const handleDateTypeUpdate = useCallback((dateType: CaseAnalyticsFilterDateType) => {
    props.setCaseAnalyticsFilters({
      dateType: dateType,
    });
  }, []);

  const handleCaseTemplateFilterUpdate = useCallback((caseTemplateIds: string[]) => {
    props.setCaseAnalyticsFilters({
      caseTemplateIds: caseTemplateIds,
    });
  }, []);

  const handleCaseTagFilterUpdate = useCallback((caseTagIds: string[]) => {
    props.setCaseAnalyticsFilters({
      caseTagIds: caseTagIds,
    });
  }, []);

  const handleAssigneeFilterUpdate = useCallback(
    (userAssigneeIds: string[], teamAssigneeIds: string[]) => {
      props.setCaseAnalyticsFilters({
        assigneeIds: userAssigneeIds,
        teamAssigneeIds,
      });
    },
    [],
  );

  const handleResolverFilterUpdate = useCallback(
    (userResolverIds: string[], teamResolverIds: string[]) => {
      props.setCaseAnalyticsFilters({
        resolverIds: userResolverIds,
        teamResolverIds: teamResolverIds,
      });
    },
    [],
  );

  const handleStatusFilterChange = useCallback((stateIds: string[]) => {
    props.setCaseAnalyticsFilters({
      caseStateIds: stateIds,
    });
  }, []);

  const handleDateUpdateOrClearV2 = useCallback(
    (range: RangeValue<DateValue> | null) => {
      updateDateFilters(range, handleDateUpdate);
    },
    [handleDateUpdate],
  );

  const loadingFilterData =
    loadingCaseTemplateBasicsOptions || loadingTags || loadingUsers || loadingTeams;

  return (
    <DashboardCaseAnalyticsResolutionsView
      caseResolutionDistribution={props.caseResolutionDistribution}
      caseTagDistribution={props.caseTagDistribution}
      caseResolutionsByTag={props.caseResolutionsByTag}
      activeCaseTemplateBasicsOptions={props.activeCaseTemplateBasicsOptions}
      tags={props.tags}
      users={props.users}
      teams={props.teams}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      queriedAt={props.queriedAt}
      updatedAt={props.updatedAt}
      filters={props.filters}
      loadingDistribution={loadingDistribution || loadingFilterData}
      loadingTagDistribution={loadingTagDistribution || loadingFilterData}
      loadingResolutionByTag={loadingResolutionByTag || loadingFilterData}
      loading={
        loadingDistribution || loadingTagDistribution || loadingResolutionByTag || loadingFilterData
      }
      loadResolutionsAnalyticsDistribution={loadCaseResolutionsAnalyticsDistribution}
      loadResolutionsAnalyticsTagDistribution={loadCaseResolutionsAnalyticsTagDistribution}
      loadResolutionsAnalyticsByTag={loadCaseResolutionsAnalyticsByTag}
      handleDateUpdateV2={handleDateUpdateOrClearV2}
      handleDateTypeUpdate={handleDateTypeUpdate}
      handleCaseTemplateFilterUpdate={handleCaseTemplateFilterUpdate}
      handleCaseTagFilterUpdate={handleCaseTagFilterUpdate}
      handleAssigneeFilterUpdate={handleAssigneeFilterUpdate}
      handleResolverFilterUpdate={handleResolverFilterUpdate}
      handleStatusFilterChange={handleStatusFilterChange}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  currentEnvironment: state.currentEnvironment.currentEnvironment,
  tags: state.tags.tags.case,
  caseResolutionDistribution: state.caseAnalytics.caseResolutionDistribution,
  caseTagDistribution: state.caseAnalytics.caseTagDistribution,
  caseResolutionsByTag: state.caseAnalytics.caseResolutionsByTag,
  filters: getAdjustedCaseAnalyticsFilters(state),
  updatedAt: state.caseAnalytics.updatedAt,
  queriedAt: state.caseAnalytics.queriedAt.caseResolutions,
  activeCaseTemplateBasicsOptions: state.caseTemplates.basicsOptions.filter(
    (option) => option.status === CaseTemplateStatus.Active,
  ),
  users: state.user.users,
  teams: state.teams.teams,
});

const mapDispatchToProps = {
  loadTags,
  loadUserList,
  loadTeams,
  loadCaseAnalyticsResolutionDistribution,
  loadCaseAnalyticsTagDistribution,
  loadCaseAnalyticsResolutionsByTag,
  loadCaseTemplateBasicsOptions,
  setCaseAnalyticsFilters,
  resetCaseAnalyticsDateFilter,
  clearCaseAnalytics,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardCaseAnalyticsResolutionsRoute);
